import clsx from 'clsx';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/core/Checkbox.atom';
import { ClearableInput } from '@/core/ClearableInput.molecule';
import { IconSelect } from '@/core/IconSelect.molecule';
import { SortIcon } from '@/core/SortIcon.atom';
import { DateCell } from '@/homescreen/CellRender.atom';
import { Icon } from '@seeqdev/qomponents';
import type { RefObject } from 'react';
import type { ReactSelectOption } from '@/core/IconSelect.molecule';
import type { IconSelectProps } from '@/formbuilder/IconSelectFormComponent.atom';

export const CELL_TYPES = {
  CHECKMARK: 'checkmark',
  /**
   * Every table can only have one column of type `ROW_SELECTION`. `ROW_SELECTION` is
   * a checkbox that allows the selection of a row as well as renders a header with a `select all` checkbox
   */
  ROW_SELECTION: 'rowSelection',
  DATE_TIME: 'dateTime',
  ENABLED_DISABLED: 'enabledDisabled',
  DROPDOWN: 'dropdown',
  YES_NO_NOTAPPLICABLE: 'yesNoNotApplicable', // you must pass in your own cellRenderFunction
};

export interface TableColumn<T = any> {
  // the accessor is used to match up cell content with column. so if you have an array of items that looks like
  // this : [{id: '1', name: 'Jane', email: 'jane@seeq.com'}, id: '2', name: 'Joe', email: 'joe@seeq.com'}]
  // you would specify accessor: 'name' to add a column to displays Jane and Joe
  accessor: string;
  // optional header - this is the text that is displayed in the table header. Note that ROW_SELECTION columns don't
  // render any text
  header?: string;
  // if your cell is not a text, CHECKMARK or ROW_SELECTION you can provide your own render function
  cellRenderFunction?: (item: T, accessor?: keyof T) => React.ReactNode;
  // if you desire a more interesting header you can render your own by providing a headerRenderFunction
  headerRenderFunction?: (column: TableColumn<T>) => React.ReactNode;
  // default filters not enough - you can provide your own
  headerFilterFunction?: (column: TableColumn<T>) => React.ReactNode;
  // cellStyle can be any css - but typically you want to provide things like width/min-width/max-width
  cellStyle?: React.CSSProperties;
  // headerStyle can be any css - typically used for header sizing independent of body cell sizing
  headerStyle?: React.CSSProperties;
  // cellType: one of CELL_TYPES, if none is provided plain text is assumed (unless you provide a custom render
  // function)
  cellType?: string;
  // should this column be filterable?
  filterable?: boolean;
  // should this column be sortable?
  sortable?: boolean;
  // string representing the search term for the field on the backend, they do not necessarily match the accessors
  searchProperty?: string;
  // if present, when pressing enter or the checkmark in the filter field, this callback will be invoked
  enterCallback?: () => void;
  // if present, adds an icon with the specified text as a tooltip
  tooltip?: string;
  // options for the header filter if header type is DROPDOWN
  dropdownOptions?: {
    value: any;
    text: string;
    icon?: string;
  }[];
  // if false, Resizer element is omitted
  isResizable?: boolean;
  // if true, omits the column from the table
  excludeIf?: boolean;
}

type ColumnWidthMap = Record<string, number>;

const getColumnKey = (column: TableColumn, idx: number) => `${idx}_${column.accessor}`;
const getColumnHeaderTestId = (column: TableColumn) => `tableHeader_${column.searchProperty ?? column.accessor}`;

const getColumnStyle = (
  style: React.CSSProperties | undefined,
  columnWidth: number | undefined,
): React.CSSProperties | undefined => {
  if (_.isNil(columnWidth)) {
    return style;
  }

  return {
    ...style,
    width: columnWidth,
  };
};

interface TableProps {
  testId?: string;
  columns: TableColumn[];
  items: Record<string, any>[];
  empty?: React.ReactNode;
  tableClass?: string;
  selectedIds?: string[];
  onRowClickCallback?: (
    item: {
      id: string;
    },
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
  ) => void;
  onRowSelectCallback?: (item: any) => void;
  isRowSelectRadio?: boolean;
  filterTableCallback?: (
    option: {
      value: any;
    },
    field: string,
  ) => void;
  selectAllCallback?: () => void;
  sortTableCallback?: (field: string, order: boolean) => void;
  sortProperty?: string;
  sortAscending?: boolean;
  selectAll?: boolean;
  searchParams?: Record<string, any>;
  headerClass?: string;
  stickyHeader?: boolean;
  stickyHeaderTop?: number;
  showCellBorder?: boolean;
  rowWrapper?: (
    rowCreator: (props: {
      refSetter?: (e: HTMLTableRowElement | null) => void;
      extraClassNames?: string;
    }) => React.JSX.Element,
    item: any,
  ) => React.ReactNode;
}

interface ResizerProps {
  columnKey: string;
  thRef: RefObject<HTMLTableCellElement | null>;
  onMouseDown: (columnKey: string, ref: RefObject<HTMLTableCellElement | null>) => void;
  setStartOffset: (offset: number) => void;
}

/**
 * Resizer is a layer that enabled the dragging of the table headers.
 */
export const Resizer = (props: ResizerProps) => {
  const { columnKey, thRef, setStartOffset } = props;
  return (
    <div
      onMouseDown={(e) => {
        props.onMouseDown(columnKey, thRef);
        if (thRef.current) {
          setStartOffset(thRef.current.offsetWidth - e.pageX);
        }
      }}
      className="tableHeaderResizer"
    />
  );
};

interface ResizableTableHeaderProps {
  column: TableColumn;
  columnKey: string;
  columnWidth?: number;
  idx: number;
  onMouseDown: (columnKey: string, ref: RefObject<HTMLTableCellElement | null>) => void;
  setStartOffset: (offset: number) => void;
  renderTableHeader: (col: TableColumn) => React.ReactNode;
}

/**
 * Table Headers are broken out into their own little component to avoid having to create the useRefs in a loop ...
 */
export const ResizableTableHeader = (props: ResizableTableHeaderProps) => {
  const { column, columnKey, columnWidth, idx, onMouseDown, setStartOffset, renderTableHeader } = props;
  const ref = useRef<HTMLTableCellElement>(null);

  return (
    <th
      className="tw:!bg-[var(--sq-tools-background)]"
      key={`row_${idx}`}
      ref={ref}
      data-testid={getColumnHeaderTestId(column)}
      style={getColumnStyle(column.headerStyle, columnWidth)}>
      <div style={{ position: 'relative' }}>
        {_.isFunction(column.headerRenderFunction) ? column.headerRenderFunction(column) : renderTableHeader(column)}
        {(column.isResizable === undefined || column.isResizable) && (
          <Resizer columnKey={columnKey} thRef={ref} onMouseDown={onMouseDown} setStartOffset={setStartOffset} />
        )}
      </div>
    </th>
  );
};

interface TableHeadProps {
  columns: TableColumn[];
  columnWidths: ColumnWidthMap;
  headerClass?: string;
  onMouseDown: (columnKey: string, ref: RefObject<HTMLTableCellElement | null>) => void;
  renderTableHeader: (column: TableColumn) => React.ReactNode;
  setStartOffset: (offset: number) => void;
}

const TableHead = ({
  columns,
  columnWidths,
  headerClass,
  onMouseDown,
  renderTableHeader,
  setStartOffset,
}: TableHeadProps) => (
  <thead className={headerClass} data-testid="resizable-table-header">
    <tr>
      {_.map(columns, (column, idx) => {
        const columnKey = getColumnKey(column, idx);

        return (
          <ResizableTableHeader
            column={column}
            columnKey={columnKey}
            columnWidth={columnWidths[columnKey]}
            idx={idx}
            key={columnKey}
            onMouseDown={onMouseDown}
            renderTableHeader={renderTableHeader}
            setStartOffset={setStartOffset}
          />
        );
      })}
    </tr>
  </thead>
);

export interface TableRowProps {
  item: any;
  onRowClickCallback?: (item: any, event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void;
  columns: TableColumn[];
  columnWidths: ColumnWidthMap;
  showCellBorder?: boolean;
  rowWrapper?: (
    rowCreator: (props: { ref?: (e: HTMLTableRowElement) => void; extraClassNames?: string }) => React.JSX.Element,
    item: any,
  ) => React.ReactNode;
}

export interface TableCellProps {
  item: any;
  column: TableColumn;
  columnWidth?: number;
  idx: number;
  showCellBorder?: boolean;
}

const TableCell = ({ item, column, columnWidth, idx, showCellBorder = false }: TableCellProps) => {
  return (
    <td
      data-testid={`row_${item.id}_col_${idx}`}
      style={getColumnStyle(column.cellStyle, columnWidth)}
      className={`specWorkbenchItemCell_${column.accessor}_${item.id} ${showCellBorder ? 'bordered-cell' : ''}`}>
      {_.isFunction(column.cellRenderFunction)
        ? column.cellRenderFunction(item, column.accessor)
        : _.get(item, column.accessor)}
    </td>
  );
};

const TableRow = ({
  item,
  onRowClickCallback,
  columns,
  columnWidths,
  showCellBorder = false,
  rowWrapper,
}: TableRowProps) => {
  const rowCreator: (props: {
    refSetter?: (e: HTMLTableRowElement) => void;
    extraClassNames?: string;
  }) => React.JSX.Element = ({ refSetter, extraClassNames }) => (
    <tr
      ref={refSetter}
      className={clsx('hoverTable specWorkbenchItemRow', extraClassNames)}
      onClick={(e) => (_.isFunction(onRowClickCallback) ? onRowClickCallback(item, e) : _.noop)}>
      {_.map(_.reject(columns, 'excludeIf'), (column, idx) => (
        <TableCell
          key={`row_${item.id}_col_${idx}`}
          item={item}
          column={column}
          columnWidth={columnWidths[getColumnKey(column, idx)]}
          idx={idx}
          showCellBorder={showCellBorder}
        />
      ))}
    </tr>
  );
  return (rowWrapper ? rowWrapper(rowCreator, item) : rowCreator({})) as React.JSX.Element;
};

/** Generic table element with sorting, filtering, searching capabilities */
export const Table = (props: TableProps) => {
  const {
    testId,
    tableClass,
    columns,
    items,
    empty,
    selectedIds,
    sortProperty,
    sortAscending,
    selectAll = false,
    searchParams,
    onRowClickCallback,
    onRowSelectCallback,
    isRowSelectRadio = false,
    filterTableCallback,
    sortTableCallback,
    selectAllCallback,
    headerClass,
    stickyHeader = false,
    stickyHeaderTop = 0,
    showCellBorder = false,
    rowWrapper,
  } = props;

  const { t } = useTranslation();

  const [columnWidths, setColumnWidths] = useState<ColumnWidthMap>({});
  const activeColumnKeyRef = useRef<string | null>(null);
  const activeHeaderRef = useRef<RefObject<HTMLTableCellElement | null> | null>(null);
  const startOffsetRef = useRef<number>(-1);
  const tableRef = useRef<HTMLTableElement>(null);

  const setStartOffset = (offset: number) => {
    startOffsetRef.current = offset;
  };

  useEffect(() => {
    const applyColumnWidth = (columnKey: string, width: number) => {
      const activeHeader = activeHeaderRef.current?.current;
      if (!activeHeader) {
        return;
      }

      activeHeader.style.width = `${width}px`;

      const columnIndex = Number(columnKey.split('_')[0]) + 1;
      tableRef.current?.querySelectorAll(`tbody tr td:nth-child(${columnIndex})`).forEach((cell) => {
        (cell as HTMLTableCellElement).style.width = `${width}px`;
      });
    };

    const handleMouseMove = (e: MouseEvent) => {
      const activeColumnKey = activeColumnKeyRef.current;
      if (activeColumnKey) {
        applyColumnWidth(activeColumnKey, Math.max(0, startOffsetRef.current + e.pageX));
      }
    };

    const handleMouseUp = () => {
      const activeColumnKey = activeColumnKeyRef.current;
      const activeHeader = activeHeaderRef.current?.current;

      if (activeColumnKey && activeHeader) {
        const nextWidth = activeHeader.offsetWidth;

        setColumnWidths((previousColumnWidths) =>
          previousColumnWidths[activeColumnKey] === nextWidth
            ? previousColumnWidths
            : {
                ...previousColumnWidths,
                [activeColumnKey]: nextWidth,
              },
        );
      }

      activeColumnKeyRef.current = null;
      activeHeaderRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const onMouseDown = (columnKey: string, thRef: RefObject<HTMLTableCellElement | null>) => {
    if (!thRef.current) {
      return;
    }

    activeColumnKeyRef.current = columnKey;
    activeHeaderRef.current = thRef;
  };

  const renderCheckmark = (item: Record<any, any>, accessor?: string | number | symbol) => {
    const value = accessor === undefined ? undefined : item[accessor as string];

    return (
      <div className="flexColumnContainer flexCenter">
        <span className={clsx(value ? 'fa-sharp fa-regular fa-check text-success' : 'fa-sharp fa-regular fa-close')} />
      </div>
    );
  };

  const renderRowSelect = (item: Record<any, any>) => (
    <div className="flexColumnContainer flexCenter rowSelect">
      <Checkbox
        id={`rowSelect_${item.id}`}
        onClick={() => onRowSelectCallback?.(item)}
        isChecked={_.indexOf(selectedIds, item.id) > -1}
        type={isRowSelectRadio ? 'radio' : 'checkbox'}
      />
    </div>
  );

  const renderEnabledDisabled = (item: Record<any, any>, accessor?: string | number | symbol) => {
    const value = accessor === undefined ? undefined : item[accessor as string];

    return (
      <div className="flexColumnContainer">
        <span>{t(value ? 'ADMIN.ENABLED' : 'ADMIN.DISABLED')}</span>
      </div>
    );
  };

  const tableColumns: TableColumn[] = _.map(_.reject(columns, 'excludeIf'), (column) => {
    switch (column.cellType) {
      case CELL_TYPES.CHECKMARK:
        return { ...column, cellRenderFunction: renderCheckmark };
      case CELL_TYPES.ROW_SELECTION:
        return { ...column, cellRenderFunction: renderRowSelect };
      case CELL_TYPES.ENABLED_DISABLED:
        return { ...column, cellRenderFunction: renderEnabledDisabled };
      case CELL_TYPES.DATE_TIME:
        return {
          ...column,
          cellRenderFunction: (item: Record<any, any>, accessor?: string | number | symbol) => (
            <DateCell item={item} accessor={accessor as string} />
          ),
        };
      default:
        return column;
    }
  });

  const doFilterTable = (field: string, value: string) => {
    if (value !== '' || _.has(searchParams, field)) {
      filterTableCallback?.({ value }, field);
    }
  };

  const renderTextInputFilter = (column: TableColumn) => (
    <ClearableInput
      field={column.searchProperty ?? ''}
      searchValue={searchParams?.[column.searchProperty as string]}
      onInputChange={doFilterTable}
      enterCallback={column.enterCallback}
    />
  );

  const checkmarkFilterOptions = [
    { text: '-', value: null },
    { text: 'ADMIN.YES', icon: 'fa fa-check text-success', value: true },
    { text: 'ADMIN.NO', icon: 'fa fa-close', value: false },
  ];

  const enabledDisabledFilterOptions = [
    { text: '-', value: undefined },
    { text: 'ADMIN.ENABLED', value: true },
    { text: 'ADMIN.DISABLED', value: false },
  ];

  const yesNoNAFilterOptions = [
    { text: '-', value: '' },
    { text: 'ADMIN.YES', value: 'yes' },
    { text: 'ADMIN.NO', value: 'no' },
    { text: 'ADMIN.NOT_APPLICABLE', value: 'na' },
  ];

  const renderDropdownFilter = (column: TableColumn, filterOptions: ReactSelectOption[]) => (
    <IconSelect
      selectOptions={filterOptions}
      name={column.searchProperty}
      extraClassNames={`spec_${column.searchProperty}_filter fontWeightNormal`}
      onChange={filterTableCallback as IconSelectProps['onChange']}
      value={searchParams?.[column.searchProperty as string]}
    />
  );

  const renderNoFilter = (
    <div className="positionRelative flexFill">
      {_.some(columns, 'filterable') && <div className="width-maximum form-control disabledTableFilter" />}
    </div>
  );

  const renderFilter = (column: TableColumn) => {
    if (!column.filterable) {
      return renderNoFilter;
    } else {
      let filter: React.ReactNode = null;
      if (column.headerFilterFunction) {
        filter = column.headerFilterFunction(column);
      } else if (column.cellType === CELL_TYPES.CHECKMARK) {
        filter = renderDropdownFilter(column, checkmarkFilterOptions as ReactSelectOption[]);
      } else if (column.cellType === CELL_TYPES.YES_NO_NOTAPPLICABLE) {
        filter = renderDropdownFilter(column, yesNoNAFilterOptions);
      } else if (column.cellType === CELL_TYPES.ENABLED_DISABLED) {
        filter = renderDropdownFilter(column, enabledDisabledFilterOptions as ReactSelectOption[]);
      } else if (column.cellType === CELL_TYPES.DROPDOWN) {
        filter = renderDropdownFilter(column, [{ text: '-', value: undefined }, ...(column.dropdownOptions ?? [])]);
      } else {
        filter = renderTextInputFilter(column);
      }

      return <>{filter}</>;
    }
  };

  const renderTableHeader = (column: TableColumn) => {
    if (column.cellType === CELL_TYPES.ROW_SELECTION) {
      if (!_.isNil(selectAllCallback)) {
        return (
          <div className="flexRowContainer flexAlignCenter">
            <Checkbox
              id="selectAllRows"
              onClick={selectAllCallback}
              isChecked={selectAll}
              flexAlign="center"
              classes="flexCenter selectAllRows"
            />
          </div>
        );
      }
    } else if (column.header) {
      return (
        <div className="flexRowContainer">
          {column.sortable ? (
            <div
              data-testid={`${column.accessor}_header`}
              className="flexColumnContainer cursorPointer"
              onClick={() => sortTableCallback?.(column.accessor, !!sortAscending)}>
              {t(column.header)}
              {column.tooltip && (
                <Icon tooltip={t(column.tooltip)} extraClassNames="pl5" icon="fa-question-circle" large={true} />
              )}
              <div data-testid={`${column.accessor}_sort`}>
                <SortIcon sortAsc={!!sortAscending} sortProperty={column.accessor} sortBy={sortProperty as string} />
              </div>
            </div>
          ) : (
            <div data-testid={`${column.accessor}_header`} className="flexColumnContainer cursorNotAllowed">
              {t(column.header)}
              {column.tooltip && (
                <Icon tooltip={column.tooltip} extraClassNames="pl5" icon="fa-question-circle" large={true} />
              )}
            </div>
          )}
          {renderFilter(column)}
        </div>
      );
    }
  };

  const hasEmptyContent = _.isEmpty(items) && !_.isNil(empty);

  const appliedTableClasses = clsx(
    'table',
    'table-no-top-border',
    'width-99-percent',
    'table-striped',
    'pb65',
    {
      'tw:[&_thead_th]:sticky tw:[&_thead_th]:z-[1] tw:[&_thead_th]:border-y-0! tw:[&_thead_th]:shadow-[0_1px_0_rgba(0,0,0,0.12)] tw:[&_thead_th]:top-[var(--table-sticky-header-top,0px)]':
        stickyHeader,
      'tw:h-full tw:mb-0 tw:[&_thead]:relative tw:[&_thead]:z-[1] tw:[&_tbody]:relative tw:[&_tbody]:z-[1] tw:[&_tbody>tr:hover]:bg-transparent! tw:[&_tbody>tr>td]:border-t-0! tw:[&_tbody>tr>td]:p-0!':
        hasEmptyContent,
    },
    tableClass,
  );

  const appliedTableStyle = stickyHeader
    ? ({ '--table-sticky-header-top': `${stickyHeaderTop}px` } as React.CSSProperties)
    : undefined;

  const tableData = React.useMemo(
    () =>
      _.map(items, (item) => (
        <TableRow
          key={`row_${item.id}`}
          item={item}
          onRowClickCallback={onRowClickCallback}
          columns={tableColumns}
          columnWidths={columnWidths}
          showCellBorder={showCellBorder}
          rowWrapper={rowWrapper}
        />
      )),
    [columnWidths, items, onRowClickCallback, rowWrapper, showCellBorder, tableColumns],
  );

  const emptyContent = hasEmptyContent ? (
    <tr className="tw:hover:bg-transparent!">
      <td colSpan={tableColumns.length} className="tw:h-full tw:border-t-0! tw:p-0!">
        <div className="tw:mx-auto tw:flex tw:h-full tw:w-full tw:max-w-full tw:items-center tw:justify-center tw:bg-transparent tw:px-4 tw:pb-8 tw:pt-6">
          {empty}
        </div>
      </td>
    </tr>
  ) : null;

  return (
    <>
      <table ref={tableRef} className={appliedTableClasses} data-testid={testId} style={appliedTableStyle}>
        <TableHead
          columns={tableColumns}
          columnWidths={columnWidths}
          headerClass={headerClass}
          onMouseDown={onMouseDown}
          renderTableHeader={renderTableHeader}
          setStartOffset={setStartOffset}
        />
        <tbody className={clsx({ hoverTable: !hasEmptyContent })}>{hasEmptyContent ? emptyContent : tableData}</tbody>
      </table>
    </>
  );
};
